import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import KPI from '../../../../ui/kpi/KPI'
import { Widget } from '../../../../widget/Widget'
import { WidgetGrid } from '../../../../widget/WidgetGrid'

import { useActiveMembersWidget } from '../../../../../hooks/widget/users/useActiveMembersWidget'
import { useLockedUsersWidget } from '../../../../../hooks/widget/users/useLockedUsersWidget'
import { useManagersWidget } from '../../../../../hooks/widget/users/useManagersWidget'
import { useTotalUsersWidget } from '../../../../../hooks/widget/users/useTotalUsersWidget'

import { useApp } from '../../../../../contexts/AppContext'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import { StatisticValueMetadata } from '../../../../../types/court/court'
import { WidgetTone } from '../../../../../types/widget/widget'

type UsersKpisProps = {
  refreshTrigger?: number
}

export const UsersKpis = ({ refreshTrigger }: UsersKpisProps) => {
  const { t } = useTranslation('users')
  const { showBanner } = useApp()
  const totalUsersResult = useTotalUsersWidget()
  const activeMembersResult = useActiveMembersWidget()
  const managersResult = useManagersWidget()
  const lockedUsersResult = useLockedUsersWidget()

  const handleError = useCallback(
    (message: string) => showBanner({ kind: BannerKind.ERR, msg: message }),
    [showBanner]
  )

  return (
    <WidgetGrid>
      <Widget<StatisticValueMetadata>
        result={totalUsersResult}
        className="col-12 col-md-6 col-xl-3"
        onError={handleError}
        refreshTrigger={refreshTrigger}
      >
        {data => (
          <KPI
            label={t('users.kpi.total')}
            value={data?.value ?? '—'}
            delta={data?.changePercentage ?? undefined}
            sub={t('users.kpi.comparison')}
            deltaFallback={t('users.kpi.no-previous-data')}
          />
        )}
      </Widget>

      <Widget<StatisticValueMetadata>
        result={activeMembersResult}
        className="col-12 col-md-6 col-xl-3"
        tone={WidgetTone.ACCENT}
        onError={handleError}
        refreshTrigger={refreshTrigger}
      >
        {data => (
          <KPI
            label={t('users.kpi.active')}
            value={data?.value ?? '—'}
            delta={data?.changePercentage ?? undefined}
            sub={t('users.kpi.comparison')}
            deltaFallback={t('users.kpi.no-previous-data')}
          />
        )}
      </Widget>

      <Widget<StatisticValueMetadata>
        result={managersResult}
        className="col-12 col-md-6 col-xl-3"
        onError={handleError}
        refreshTrigger={refreshTrigger}
      >
        {data => (
          <KPI label={t('users.kpi.managers')} value={data?.value ?? '—'} />
        )}
      </Widget>

      <Widget<StatisticValueMetadata>
        result={lockedUsersResult}
        className="col-12 col-md-6 col-xl-3"
        tone={WidgetTone.DARK}
        onError={handleError}
        refreshTrigger={refreshTrigger}
      >
        {data => (
          <KPI
            label={t('users.kpi.locked')}
            value={data?.value ?? '—'}
          />
        )}
      </Widget>
    </WidgetGrid>
  )
}
